import { useEffect, useState } from 'react';
import { Box, Skeleton, Switch, Typography } from '@mui/material';
import { useToast } from '@/hooks/toast';
import { useBusiness } from 'providers/context/BusinessProvider';
import { useNotificationPreferences, useUpdateNotificationPreferences } from '@/apis/notificationPreferences/hook';
import {
  NotificationPreferenceScope,
  NotificationPreferenceValues,
  NotificationPreferencesResponse
} from '@/apis/notificationPreferences/types';

type Draft = Record<string, NotificationPreferenceValues>;

const toggleColumnWidth = 96;

const columnHeaderSx = {
  fontSize: 11,
  fontWeight: 500,
  lineHeight: '16px',
  letterSpacing: '0.04em',
  color: '#8A8A8A'
};

const groupLabelSx = {
  fontSize: 10,
  fontWeight: 600,
  lineHeight: '16px',
  color: '#1C1C1C'
};

const itemTitleSx = {
  fontSize: 14,
  fontWeight: 600,
  lineHeight: '20px',
  color: '#1C1C1C'
};

const itemDescriptionSx = {
  mt: '2px',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: '18px',
  color: '#959595'
};

const switchSx = {
  width: 36,
  height: 20,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: '2px',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': { backgroundColor: '#2563EB', opacity: 1 },
      '& .MuiSwitch-thumb': { backgroundColor: '#fff' }
    },
    '&:not(.Mui-checked) .MuiSwitch-thumb': { backgroundColor: '#fff' },
    '&:not(.Mui-checked) + .MuiSwitch-track': { backgroundColor: '#C4C4C4', opacity: 1 }
  },
  '& .MuiSwitch-thumb': {
    width: 16,
    height: 16,
    boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
  },
  '& .MuiSwitch-track': {
    borderRadius: 10,
    opacity: 1,
    backgroundColor: '#C4C4C4'
  }
};

function draftFrom(data: NotificationPreferencesResponse): Draft {
  return Object.fromEntries(
    data.groups.flatMap((group) => group.items.map((item) => [item.key, { in_app: item.in_app, email: item.email }]))
  );
}

export default function NotificationPreferences({ scope }: { scope: NotificationPreferenceScope }) {
  const { selectedBusiness } = useBusiness();
  const businessId = scope === 'business' ? selectedBusiness : null;
  const { data, isLoading, isError } = useNotificationPreferences(scope, businessId);
  const { mutateAsync: savePreferences } = useUpdateNotificationPreferences(scope, businessId);
  const { showToast, ToastComponent } = useToast();
  const [draft, setDraft] = useState<Draft>({});

  useEffect(() => {
    if (data) setDraft(draftFrom(data));
  }, [data]);

  const onToggle = async (
    key: string,
    channel: keyof NotificationPreferenceValues,
    checked: boolean,
    currentValues: NotificationPreferenceValues
  ) => {
    const next = { ...currentValues, [channel]: checked };
    setDraft((current) => ({ ...current, [key]: next }));
    try {
      await savePreferences({ [key]: next });
      showToast('Notification preferences saved', 'success');
    } catch {
      setDraft((current) => ({ ...current, [key]: currentValues }));
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `minmax(0, 1fr) ${toggleColumnWidth}px ${toggleColumnWidth}px`,
            alignItems: 'center',
            px: 2.5,
            py: 1.5,
            borderBottom: '1px solid #EDEDED'
          }}
        >
          <Skeleton variant='text' width={120} height={16} />
          <Box display='flex' justifyContent='center'>
            <Skeleton variant='text' width={40} height={16} />
          </Box>
          <Box display='flex' justifyContent='center'>
            <Skeleton variant='text' width={40} height={16} />
          </Box>
        </Box>
        {Array.from({ length: 8 }).map((_, index) => (
          <Box
            key={index}
            sx={{
              display: 'grid',
              gridTemplateColumns: `minmax(0, 1fr) ${toggleColumnWidth}px ${toggleColumnWidth}px`,
              alignItems: 'center',
              px: 2.5,
              py: 2,
              minHeight: 72,
              borderTop: index === 0 ? undefined : '1px solid #EDEDED'
            }}
          >
            <Box sx={{ pr: 3, minWidth: 0 }}>
              <Skeleton variant='text' width={`${50 + (index % 3) * 12}%`} height={20} />
              <Skeleton variant='text' width={`${75 + (index % 2) * 10}%`} height={16} sx={{ mt: '2px' }} />
            </Box>
            <Box display='flex' justifyContent='center'>
              <Skeleton variant='rounded' width={36} height={20} sx={{ borderRadius: 10 }} />
            </Box>
            <Box display='flex' justifyContent='center'>
              <Skeleton variant='rounded' width={36} height={20} sx={{ borderRadius: 10 }} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Typography color='error' sx={{ m: 3 }}>
        Notification preferences could not be loaded.
      </Typography>
    );
  }

  return (
    <Box>
      {ToastComponent}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `minmax(0, 1fr) ${toggleColumnWidth}px ${toggleColumnWidth}px`,
          alignItems: 'center',
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid #EDEDED'
        }}
      >
        <Typography sx={columnHeaderSx}>NOTIFICATION TYPE</Typography>
        <Typography sx={{ ...columnHeaderSx, textAlign: 'center' }}>IN-APP</Typography>
        <Typography sx={{ ...columnHeaderSx, textAlign: 'center' }}>EMAIL</Typography>
      </Box>
      {data.groups.map((group) => (
        <Box key={group.key}>
          {group.label ? (
            <Box sx={{ px: 2.5, py: 0.75, bgcolor: '#EEF5FD' }}>
              <Typography sx={groupLabelSx}>{group.label.toUpperCase()}</Typography>
            </Box>
          ) : null}
          {group.items.map((item, index) => {
            const values = draft[item.key] ?? { in_app: item.in_app, email: item.email };
            return (
              <Box
                key={item.key}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `minmax(0, 1fr) ${toggleColumnWidth}px ${toggleColumnWidth}px`,
                  alignItems: 'center',
                  px: 2.5,
                  py: 2,
                  minHeight: 72,
                  borderTop: index === 0 && !group.label ? undefined : '1px solid #EDEDED'
                }}
              >
                <Box sx={{ pr: 3, minWidth: 0 }}>
                  <Typography sx={itemTitleSx}>{item.label}</Typography>
                  {item.description && <Typography sx={itemDescriptionSx}>{item.description}</Typography>}
                </Box>
                <Box display='flex' justifyContent='center'>
                  <Switch
                    disableRipple
                    sx={switchSx}
                    checked={values.in_app}
                    onChange={(_, checked) => onToggle(item.key, 'in_app', checked, values)}
                    inputProps={{ 'aria-label': `${item.label} in-app` }}
                  />
                </Box>
                <Box display='flex' justifyContent='center'>
                  <Switch
                    disableRipple
                    sx={switchSx}
                    checked={values.email}
                    onChange={(_, checked) => onToggle(item.key, 'email', checked, values)}
                    inputProps={{ 'aria-label': `${item.label} email` }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}
